import usePageSEO from '../hooks/usePageSEO';
import { EventSchema } from '../components/JsonLd';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Footer from '../components/Footer';
import { 
  ArrowRight, 
  Calendar,
  Clock,
  Users,
  CheckCircle2,
  Zap,
  FileText,
  BarChart3,
  Bot,
  ShieldCheck,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Webinar date: June 21st, 2026 at 12:00 PM EST
const WEBINAR_DATE = new Date('2026-06-21T12:00:00-05:00');

const agendaItems = [
  {
    time: '12:00 PM',
    title: 'The AI Landscape for Finance in 2026',
    description: 'What\'s real and what\'s hype — a no-BS overview of GPT, Claude, Gemini, and where each model excels for finance work.',
    icon: TrendingUp,
  },
  {
    time: '12:10 PM',
    title: 'Prompting That Actually Works for Finance',
    description: 'Live demos: building financial models, writing variance analysis, extracting data from messy PDFs — using Claude and GPT best practices.',
    icon: FileText,
  },
  {
    time: '12:20 PM',
    title: 'LLM Workflows Your Team Can Use Monday Morning',
    description: 'Practical workflows: auto-generating board decks, reconciling GL accounts with natural language, and building audit-ready reports in minutes.',
    icon: Zap,
  },
  {
    time: '1:30 PM',
    title: 'AI Agents: What They Are and Why They Matter',
    description: 'How autonomous agents go beyond chat — executing multi-step tasks across systems without human babysitting. Real examples, not theory.',
    icon: Bot,
  },
  {
    time: '12:40 PM',
    title: 'Live Demo: An Agent Closing Invoices End-to-End',
    description: 'Furqan runs Plouton AI against a live ERP — from invoice ingestion to three-way matching to payment scheduling. No slides, real data.',
    icon: BarChart3,
  },
  {
    time: '12:50 PM',
    title: 'Open Q&A',
    description: 'Bring your hardest questions about LLMs, agents, or automation. Furqan answers live — no pre-screening.',
    icon: ShieldCheck,
  },
];

const whoIsThisFor = [
  'CFOs and VPs of Finance tired of throwing headcount at manual processes',
  'Controllers running month-end close on spreadsheets and prayers',
  'Finance Ops leaders evaluating AI but skeptical of the hype',
  'Anyone who has tried RPA and been burned by maintenance costs',
];

const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0, hours: 0, minutes: 0, seconds: 0,
  });

  useEffect(() => {
    const calculate = () => {
      const diff = targetDate.getTime() - new Date().getTime();
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      }
    };
    calculate();
    const timer = setInterval(calculate, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  const units = [
    { value: timeLeft.days, label: 'Days' },
    { value: timeLeft.hours, label: 'Hours' },
    { value: timeLeft.minutes, label: 'Min' },
    { value: timeLeft.seconds, label: 'Sec' },
  ];

  return (
    <div className="flex gap-2 sm:gap-3 justify-center">
      {units.map((unit) => (
        <div key={unit.label} className="text-center">
          <div className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl p-3 sm:p-4 min-w-[60px] sm:min-w-[80px]">
            <div className="text-2xl sm:text-4xl font-bold text-white font-heading">
              {String(unit.value).padStart(2, '0')}
            </div>
          </div>
          <div className="text-[10px] sm:text-xs text-slate-500 mt-1.5 font-medium uppercase tracking-wider">{unit.label}</div>
        </div>
      ))}
    </div>
  );
};

export const WebinarPage = () => {
  usePageSEO({
    title: 'AI for CFOs: Free Live Webinar | Plouton AI',
    description: 'Free 60-min session: practical LLM workflows for finance, live agent demo automating invoices end-to-end. June 21, 2026 at 12 PM EST.',
    path: '/webinar'
  });

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-20 overflow-hidden">
      <EventSchema
        name="AI for CFOs: From Manual Work to Automated Execution"
        description="Join Furqan Kidwai (CEO, Plouton AI) for a free 60-min live session on AI for finance — practical LLM workflows and a live agent demo."
        startDate="2026-06-21T12:00:00-05:00"
        endDate="2026-06-21T13:00:00-05:00"
        performer="Muhammad Furqan Karim Kidwai"
      />
      {/* Hero */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-950/40 via-[#0a0a0f] to-[#0a0a0f]" />
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/15 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-orange-500/8 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
              </span>
              <span className="text-xs font-semibold text-orange-400 uppercase tracking-wider">Live Webinar · Free</span>
            </div>

            {/* Headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-5 max-w-4xl mx-auto leading-[1.1]">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-300">AI for CFOs:</span>{' '}
              From Manual Work to Automated Execution
            </h1>

            <p className="text-lg sm:text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto mb-8">
              Your AP team processes invoices the same way it did in 2015. Your close takes 10 days when it should take 2.
              This session shows you exactly how to fix that — live, with a real demo, no slides.
            </p>

            {/* Meta */}
            <div className="flex flex-wrap items-center justify-center gap-5 text-sm text-slate-500 mb-10">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-slate-600" />
                June 21, 2026
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-slate-600" />
                12:00 PM EST · 60 min
              </span>
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-slate-600" />
                Limited seats
              </span>
            </div>

            {/* Countdown */}
            <div className="mb-10">
              <CountdownTimer targetDate={WEBINAR_DATE} />
            </div>

            {/* CTA */}
            <a href="#register">
              <Button 
                size="lg"
                className="bg-orange-500 text-white hover:bg-orange-600 hover:shadow-xl hover:shadow-orange-500/20 transition-all duration-300 font-semibold rounded-lg px-10 py-6 text-base"
                data-testid="webinar-hero-cta"
              >
                Save My Seat
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Your Host */}
      <section className="py-16 sm:py-20 border-t border-slate-800/50">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="flex flex-col md:flex-row items-center gap-8 md:gap-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="w-40 h-40 sm:w-44 sm:h-44 rounded-2xl bg-gradient-to-br from-blue-600 to-orange-500 p-[2px] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/j3pglu2x_1758100360741.jpeg" 
                  alt="Furqan Kidwai - CEO Plouton AI and Webinar Host" 
                  className="w-full h-full rounded-2xl object-cover"
                  width={160}
                  height={160}
                  loading="lazy"
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400 mb-2 block">Your Host</span>
              <h2 className="font-heading text-2xl font-bold text-white mb-1">
                Muhammad Furqan Karim Kidwai
              </h2>
              <p className="text-blue-400 text-sm font-medium mb-3">Co-founder & CEO, Plouton AI</p>
              <a 
                href="https://www.forbes.com/profile/plouton-ai/?list=30under30-asia-finance-venture-capital/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-slate-800/80 border border-slate-700 rounded-lg px-3 py-1.5 mb-4 hover:border-slate-600 transition-colors"
                data-testid="webinar-forbes-badge"
              >
                <span className="font-bold text-[13px] text-white tracking-tight" style={{ fontFamily: "'Georgia', serif" }}>Forbes</span>
                <span className="text-[9px] font-bold uppercase tracking-wider text-red-400 leading-none">30 Under 30 Asia</span>
              </a>
              <p className="text-slate-400 leading-relaxed">
                Furqan has spent the last decade at the intersection of finance and technology. Before founding Plouton AI, he worked with enterprise finance teams
                across SAP, Oracle, and NetSuite and saw firsthand how much time gets wasted on work that machines should be doing.
                In this session, he is not pitching. He is showing you the actual system, live, against real data.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-16 sm:py-20 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 block">
              Who This Is For
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">
              This session is for you if...
            </h2>
          </motion.div>

          <div className="space-y-3">
            {whoIsThisFor.map((item, index) => (
              <motion.div
                key={item.title}
                className="flex items-start gap-4 bg-slate-900/50 border border-slate-800 rounded-xl p-5"
                initial={{ opacity: 0, x: -15 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm sm:text-base">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Agenda */}
      <section className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 block">
              Agenda
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
              60 minutes. Zero filler.
            </h2>
            <p className="text-slate-500">Every minute is designed to give you something actionable.</p>
          </motion.div>

          <div className="space-y-3">
            {agendaItems.map((item, index) => (
              <motion.div
                key={item.time}
                className="flex gap-4 sm:gap-5 bg-slate-900/40 border border-slate-800 rounded-xl p-5 sm:p-6 hover:border-slate-700 transition-colors group"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.07 }}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                  <item.icon className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-1">
                    <h3 className="font-heading font-semibold text-white text-sm sm:text-base">{item.title}</h3>
                    <span className="text-xs text-slate-600 font-mono">{item.time} EST</span>
                  </div>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Strip */}
      <section className="py-10 border-y border-slate-800/50">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16 text-center">
            <div>
              <div className="text-2xl font-bold text-white font-heading">500+</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Finance leaders registered</div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-slate-800" />
            <div>
              <div className="text-2xl font-bold text-white font-heading">85%</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Reduction in manual work</div>
            </div>
            <div className="hidden sm:block w-px h-8 bg-slate-800" />
            <div>
              <div className="text-2xl font-bold text-white font-heading">60 min</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Of practical, no-BS content</div>
            </div>
          </div>
        </div>
      </section>

      {/* Previous Sessions — Social Proof */}
      <section className="py-16 sm:py-20 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 block">
              From Previous Sessions
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">
              Real workshops. Real results.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* SWITCH Singapore Keynote */}
            <motion.div
              className="relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/50">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/lyhmdi9w_IMG-20251030-WA0075.webp"
                    alt="Furqan Kidwai speaking at SWITCH Singapore - Enterprise Innovation Conference"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    width={600}
                    height={450}
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-white">SWITCH Singapore</p>
                  <p className="text-xs text-slate-500">Enterprise Innovation & Technology Conference</p>
                </div>
              </div>
            </motion.div>

            {/* Workshop Group */}
            <motion.div
              className="relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/50">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src="https://customer-assets.emergentagent.com/job_conversion-v2/artifacts/xtafpwdx_WhatsApp%20Image%202026-04-25%20at%2012.21.47%20%281%29.jpeg"
                    alt="Plouton AI FinOps Workshop - Team training on AI agents for finance automation"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    width={600}
                    height={450}
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-white">FinOps AI Workshop</p>
                  <p className="text-xs text-slate-500">Live training session — Chat AI vs FinOps Agents</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-16 sm:py-20" id="register">
        <div className="max-w-xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="rounded-2xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Dark header */}
            <div className="bg-slate-900 border border-slate-800 border-b-0 rounded-t-2xl px-8 pt-8 pb-6 text-center">
              <h2 className="font-heading text-2xl font-bold text-white mb-2">
                Save your seat.
              </h2>
              <p className="text-slate-500 text-sm">
                June 21, 2026 · 12:00 PM EST · Free
              </p>
            </div>

            {/* White form area */}
            <div className="bg-white rounded-b-2xl px-6 sm:px-8 pt-4 pb-6">
              <div 
                className="wj-embed-wrapper" 
                data-webinar-hash="6mnl5pa7"
                data-testid="webinarjam-embed"
                ref={(el) => {
                  if (el && !el.querySelector('script')) {
                    const script = document.createElement('script');
                    script.src = 'https://event.webinarjam.com/register/6mnl5pa7/embed-form?formButtonText=Register&formAccentColor=%23f97316&formAccentOpacity=0.95&formBgColor=%23ffffff&formBgOpacity=1';
                    el.appendChild(script);
                  }
                }}
              />

              <p className="text-[11px] text-slate-400 text-center mt-3">
                No credit card. No spam. Just a calendar invite and a reminder before we go live.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebinarPage;
